import * as React from "react"

import {DealCategory, DealsTag} from "@irati/types"
import ReduxState from "@irati/store/types/ReduxState"
import {InjectIntl, injectIntl, FormattedMessage} from "@irati/utils/lang"
import tracking from "@irati-tracking/perks/perks"
import authentication from "@irati/auth"
import connect from "@irati/store/connect"
import {SPEND} from "@irati/deals/constants/categoryTypes"
import addOrRemoveFromArray from "@irati/utils/addOrRemoveFromArray"

import CategoriesList from "./CategoriesList"
import Tags from "./Tags"
import ShowMore from "./ShowMore"
import SpendCategoryHeader from "./CategoryHeaders/SpendCategoryHeader"

import {CategoriesWrapper, CategoryTitle} from "./styled"

interface OptionalProps {
  loadFilterOptions?: Promise<void>
}

interface Props extends OptionalProps, InjectIntl {
  categories: DealCategory[]
  selectedCategory: DealCategory
  selectedTags: DealsTag[]
  onChange: (category: DealCategory, tags?: DealsTag[]) => void
  isMobile: boolean
}

interface State {
  isShowingMore: boolean
  displayShowMoreText: boolean
}

class CategoriesTags extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isShowingMore: false,
      displayShowMoreText: false
    }

    const {selectedCategory, selectedTags} = props

    if (selectedCategory.url) {
      // Don't track the ALL_CATEGORIES category
      tracking.viewCategory(selectedCategory.name)
    }
    selectedTags.forEach((tag) => {
      tracking.viewTag(tag.name)
    })
  }

  handleShowMoreClick = () => {
    this.setState({
      isShowingMore: !this.state.isShowingMore
    })
  }

  shouldDisplayShowMoreText = () => {
    this.setState({
      displayShowMoreText: true
    })
  }

  handleCategoryClick = (category: DealCategory) => {
    if (category.url && category.name !== this.props.selectedCategory.name) {
      tracking.viewCategory(category.name)
    }
    this.props.onChange(category)
  }

  handleTagClick = (tag: DealsTag) => {
    const {selectedCategory, selectedTags, onChange} = this.props

    if (!selectedTags.includes(tag)) {
      tracking.viewTag(tag.name)
    }

    // if UI shows none selected, actually all are, so just send array with this tag
    const allSelected = selectedCategory.tags.length === selectedTags.length
    if (allSelected) {
      return onChange(selectedCategory, [tag])
    }

    const tags = addOrRemoveFromArray([...selectedTags], tag, (a, b) => a.uuid === b.uuid)

    return onChange(selectedCategory, tags)
  }

  getTopBanner = () => {
    const {selectedCategory} = this.props
    const {type} = selectedCategory

    if (type === SPEND && authentication.apps("wallet.users")) {
      return <SpendCategoryHeader />
    }

    return null
  }

  render() {
    const {isShowingMore, displayShowMoreText} = this.state
    const {categories, selectedCategory, selectedTags} = this.props

    const tags = selectedCategory.tags.filter((tag) => !!tag.filterable) || []

    return (
      <div>
        <CategoriesWrapper>
          <CategoriesList
            isShowingMore={isShowingMore}
            displayShowMoreText={displayShowMoreText}
            shouldDisplayShowMoreText={this.shouldDisplayShowMoreText}
            data={categories}
            selected={selectedCategory}
            handleCategoryClick={this.handleCategoryClick}
          />
          <ShowMore
            isShowingMore={isShowingMore}
            handleShowMoreClick={this.handleShowMoreClick}
            displayShowMoreText={displayShowMoreText}
          />
          {tags?.length > 1 && (
            <>
              <CategoryTitle small marginBottom={1}>
                <FormattedMessage id="perks.categories.subcategoryListHeader" values={{categoryName: selectedCategory.name}} />
              </CategoryTitle>
              <Tags data={tags} selected={selectedTags} handleTagClick={this.handleTagClick} />
            </>
          )}
        </CategoriesWrapper>
        {this.getTopBanner()}
      </div>
    )
  }
}

const mapStateToProps = ({mq}: ReduxState) => ({isMobile: mq.lessThan.sm})

export default injectIntl(connect(mapStateToProps)(CategoriesTags))
